'use client';

import { Suspense } from 'react';
import { LobbyClient } from '@/features/lobby/LobbyClient';
import { LaunchTransitionProvider } from '@/features/lobby/launch/LaunchTransitionProvider';
import { ExternalLinkGuardProvider } from '@/features/lobby/ui/ExternalLinkGuard';

/**
 * Route-group layout shared by the boardroom lobby (`/`) and every
 * `/board/[boardId]` route.
 *
 * Hosting `LobbyClient` here (rather than inside each page) keeps the whole
 * lobby tree mounted across client-side navigations between boards — picker
 * scroll, open panels, chat, basket, music, SSE connections, and R3F canvas
 * all survive a `router.push` from `/board/A` to `/board/B`, and from `/` to
 * `/board/X`. Previously each URL owned its own `page.tsx` that rendered
 * `LobbyClient`, so every hop tore the whole tree down and remounted it,
 * which felt like a full page refresh.
 *
 * Pages under this layout export only metadata; their bodies return `null`
 * because `LobbyClient` already paints the entire viewport and reads the
 * active board id off the URL itself.
 */
export default function LobbyLayout({ children }: { children: React.ReactNode }) {
  return (
    <LaunchTransitionProvider>
      <ExternalLinkGuardProvider>
        <Suspense fallback={null}>
          <LobbyClient />
        </Suspense>
        {children}
      </ExternalLinkGuardProvider>
    </LaunchTransitionProvider>
  );
}
